import type { JSX } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import TokenOutlinedIcon from '@mui/icons-material/TokenOutlined'
import { SOURCE_LABEL, type SourceId } from '@profiler/shared'
import { JobStatusChip } from '../components/Chips'
import { PageHeader, SectionHeader } from '../components/Page'
import { Breakdown, StatTile, type BreakdownPart } from '../components/Stat'
import { EmptyState, ErrorPanel, LoadingPanel } from '../components/States'
import { capTone, formatDateTime, formatNumber } from '../format'
import { useDashboard, useMe } from '../queries'
import { usePageTitle } from '../usePageTitle'

/**
 * The front page: what we have, what is usable, what is waiting on a person,
 * and what it cost.
 *
 * Four questions, in that order, because that is the order somebody running this
 * product asks them. Everything on this page is a real stored figure — there is
 * no engagement score, no trend line over invented history, and no gauge. If a
 * number could not be acted on it is not here.
 *
 * The one non-obvious choice is which contact count leads. It is not "contacts
 * stored": a contact whose address we would not put in a mail merge is a row,
 * not a lead, and a product that measures itself in rows will happily produce
 * more rows. The headline is the mailable count, and everything else is context
 * for it.
 */

/**
 * The five states a lookup can be in — the same five the contacts list filters
 * on, and named the same way.
 *
 * FIVE, not six. `workEmailState` and `phoneState` fold `timeout` and `error`
 * into one `failed`, because to a person they are the same thing: it was asked
 * for and did not arrive, and asking again is the action either way. Listing the
 * raw statuses here instead put two rows in the legend that the data can never
 * produce — and dropped the contacts that HAD failed out of the bar entirely,
 * so the segments no longer added up to the total they were drawn against.
 */
const LOOKUP_TONES: Record<string, { label: string; tone: BreakdownPart['tone'] }> = {
  found: { label: 'Found', tone: 'good' },
  none: { label: 'Not asked', tone: 'neutral' },
  not_found: { label: 'Nothing there', tone: 'warning' },
  pending: { label: 'In flight', tone: 'info' },
  failed: { label: 'Timed out or errored', tone: 'bad' },
}

function lookupParts(counts: Record<string, number>, link: (state: string) => string): BreakdownPart[] {
  return Object.entries(LOOKUP_TONES).map(([key, meta]) => ({
    key,
    label: meta.label,
    tone: meta.tone,
    value: counts[key] ?? 0,
    to: link(key),
  }))
}

/**
 * Why an address can or cannot be mailed, in the order somebody reads it.
 *
 * The two good grades first, then the reasons an address is unusable, then the
 * ungraded. A fixed order rather than sorted by size, because the shape is the
 * point: the top two are the product working and everything under them is a
 * different kind of failure.
 *
 * Only the mailable pair carries a link. The contacts screen has ONE control for
 * work email and it does not read a per-grade key, so a link naming one would
 * silently open the unfiltered list — the failure this file already warns about
 * on the headline tile. A number that cannot be clicked beats a link that lies.
 */
const GRADE_TONES: Record<string, { label: string; tone: BreakdownPart['tone']; to?: string }> = {
  company_domain: { label: 'At the company’s own domain', tone: 'good', to: '/contacts?gate=&email=safe' },
  company_alias_domain: { label: 'At a company alias', tone: 'good', to: '/contacts?gate=&email=safe' },
  unverified_domain_mismatch: { label: 'At a different company', tone: 'warning' },
  unverified_freemail: { label: 'A personal mailbox', tone: 'warning' },
  unverified_academic_domain: { label: 'A university address', tone: 'warning' },
  unverified_no_company_domain: { label: 'No company domain to check against', tone: 'neutral' },
  rejected_disposable: { label: 'Disposable', tone: 'bad' },
  unknown: { label: 'Ungraded', tone: 'neutral' },
}

/** The bands, most senior first. The order IS the report. */
const BAND_TONES: Record<string, { label: string; tone: BreakdownPart['tone'] }> = {
  founder: { label: 'Founder', tone: 'good' },
  c_level: { label: 'C-suite', tone: 'good' },
  vp: { label: 'VP', tone: 'info' },
  head: { label: 'Head of', tone: 'info' },
  director: { label: 'Director', tone: 'info' },
  other: { label: 'Everyone else', tone: 'neutral' },
}

/**
 * A fixed vocabulary rendered as parts, dropping whatever never occurred.
 *
 * Zero-valued segments are left out rather than drawn at nothing: a legend row
 * reading "Disposable 0" is noise on every dashboard that has never seen one,
 * and a bar with eight segments where three have data is unreadable.
 */
function partsFrom(
  counts: Record<string, number>,
  spec: Record<string, { label: string; tone: BreakdownPart['tone']; to?: string }>,
  link?: (key: string) => string,
): BreakdownPart[] {
  return Object.entries(spec)
    .map(([key, meta]) => ({
      key,
      label: meta.label,
      tone: meta.tone,
      value: counts[key] ?? 0,
      ...destination(meta.to, link, key),
    }))
    .filter((part) => part.value > 0)
}

/**
 * How many companies produced nobody, one person, two, and so on.
 *
 * Drawn from zero upwards because the zero bucket IS the finding: it is the
 * majority of every list this product has run, and a distribution that hid it
 * would leave the mean looking like a small number rather than an explained one.
 */
function yieldParts(distribution: Record<string, number>): BreakdownPart[] {
  return ['0', '1', '2', '3', '4', '5+']
    .map((key) => ({
      key,
      label: key === '0' ? 'Nobody found' : `${key} ${key === '1' ? 'person' : 'people'}`,
      tone: peopleTone(key),
      value: distribution[key] ?? 0,
      ...(key === '0' ? { to: '/companies?outcome=no_people' } : {}),
    }))
    .filter((part) => part.value > 0)
}

/** Nobody found is a warning, one person is thin, more than one is fine. */
function peopleTone(key: string): BreakdownPart['tone'] {
  if (key === '0') return 'warning'
  return key === '1' ? 'info' : 'good'
}

/** Where a breakdown row links, when it links anywhere. */
function destination(
  metaTo: string | undefined,
  link: ((key: string) => string) | undefined,
  key: string,
): { to?: string } {
  if (metaTo) return { to: metaTo }
  return link ? { to: link(key) } : {}
}

export function DashboardPage(): JSX.Element {
  usePageTitle('Dashboard')
  const stats = useDashboard()
  const me = useMe()

  if (stats.isPending) return <LoadingPanel label="Reading the numbers" />
  if (stats.isError) return <ErrorPanel error={stats.error} onRetry={() => void stats.refetch()} />
  const data = stats.data
  if (!data) return <ErrorPanel error={new Error('The dashboard returned nothing')} />

  const { jobs, reviews, credits } = data
  /*
   * Every new figure read through a default, because this is a STORED document.
   *
   * The snapshot is written by a sweep that runs every few minutes, so for those
   * few minutes after a deploy the API serves one built by the PREVIOUS version
   * — without whichever fields were just added. Reaching into a shape that is
   * not there is not a blank tile, it is a white screen, and this page has been
   * taken down once already by assuming `jobs.recent` existed.
   *
   * The defaults read as "nothing yet" rather than as zero findings: an empty
   * distribution draws no bar and an empty provider map shows the empty state,
   * both of which are true of a snapshot that predates them.
   */
  const contacts = {
    ...data.contacts,
    byCountry: data.contacts?.byCountry ?? {},
    byEmailConfidence: data.contacts?.byEmailConfidence ?? {},
    bySeniority: data.contacts?.bySeniority ?? {},
  }
  const companies = {
    ...data.companies,
    fixableWebsites: data.companies?.fixableWebsites ?? 0,
    yield: data.companies?.yield ?? { searched: 0, contactsFound: 0, mean: 0, distribution: {} },
  }
  const sources = data.sources ?? { covered: 0, byProvider: {} }
  // Absent from a snapshot written before it existed; the row is left out until the next sweep.
  const reach = data.contacts?.reach ?? null
  // Reported only once the CRM has been imported; absent means "not counted", not zero.
  const fromCrm = data.contacts?.fromCrm ?? null
  const share = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0)
  const mailableShare = contacts.total > 0 ? Math.round((contacts.mailMergeSafe / contacts.total) * 100) : 0
  // Absent for a non-administrator: the server omits what the account spends,
  // the way it does on every other route. The tiles that read it are omitted
  // too rather than rendered empty.
  const capUsed =
    credits && credits.cap > 0 ? Math.min(100, Math.round((credits.used / credits.cap) * 100)) : 0

  /*
   * The two things that are WAITING on a person, surfaced above everything else.
   *
   * A review queue nobody can see is a review queue nobody works: four hundred
   * contacts sat undecided in this product because the queue lived three clicks
   * inside a job. It is the first thing on the front page now, and only when
   * there is something in it.
   */
  /*
   * The handful of countries worth naming, and one row for the rest.
   *
   * Sorted by size and cut at six: this product's contacts are overwhelmingly in
   * one country, so the full list is one long bar and a legend of ones. The
   * remainder stays as its own segment rather than being dropped, so the bar
   * still adds up to the number it is drawn against.
   */
  const countryEntries = Object.entries(contacts.byCountry).sort((a, b) => b[1] - a[1])
  const COUNTRY_TONES: Array<BreakdownPart['tone']> = ['good', 'info', 'brand', 'neutral', 'info', 'neutral']
  const otherCountries = countryEntries.slice(6).reduce((sum, [, n]) => sum + n, 0)
  const topCountries: BreakdownPart[] = [
    ...countryEntries.slice(0, 6).map(([code, value], i) => ({
      key: code,
      label: code,
      tone: COUNTRY_TONES[i] ?? ('neutral' as BreakdownPart['tone']),
      value,
      to: `/contacts?gate=&country=${encodeURIComponent(code)}`,
    })),
    ...(otherCountries > 0
      ? [
          {
            key: '__rest',
            label: 'Elsewhere',
            tone: 'neutral' as BreakdownPart['tone'],
            value: otherCountries,
          },
        ]
      : []),
  ]

  /*
   * A hit rate per source, most-asked first.
   *
   * `credits` is absent for a non-administrator because the server strips it, so
   * the row renders without it rather than showing a zero that would read as
   * "this source is free".
   */
  const providerRows = Object.entries(sources.byProvider)
    .map(([provider, row]) => ({
      provider,
      attempts: row.attempts,
      found: row.found,
      credits: row.credits,
      rate: row.attempts > 0 ? Math.round((row.found / row.attempts) * 100) : 0,
    }))
    .sort((a, b) => b.attempts - a.attempts)

  const attention: JSX.Element[] = []
  if (reviews.open > 0) {
    attention.push(
      <Alert
        key="reviews"
        severity="info"
        action={
          <Button component={RouterLink} to="/reviews" size="small" color="inherit">
            Review
          </Button>
        }
      >
        <AlertTitle>{formatNumber(reviews.open)} contacts are waiting on a decision</AlertTitle>
        Each one is a person a gate could not admit on its own. They are not in any export until somebody
        accepts them.
      </Alert>,
    )
  }
  if (credits && credits.cap > 0 && capUsed >= 80) {
    attention.push(
      <Alert
        key="cap"
        severity={capUsed >= 100 ? 'error' : 'warning'}
        action={
          me.data?.isAdmin ? (
            <Button component={RouterLink} to="/settings" size="small" color="inherit">
              Settings
            </Button>
          ) : undefined
        }
      >
        <AlertTitle>
          {capUsed >= 100
            ? 'This month’s credit cap is spent'
            : `${capUsed}% of this month’s credit cap is spent`}
        </AlertTitle>
        {formatNumber(credits?.used ?? 0)} of {formatNumber(credits?.cap ?? 0)} credits. A job that reaches
        the cap pauses rather than overspending.
      </Alert>,
    )
  }

  return (
    <Stack spacing={2.5}>
      <PageHeader
        title="Dashboard"
        description="What the product holds, how much of it can actually be used, and what it cost to get."
        meta={
          <Typography variant="caption" color="text.secondary">
            Counted {formatDateTime(data.computedAt)}. Rebuilt every few minutes from the stored rows, never
            from a running total.
          </Typography>
        }
        actions={
          <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/jobs/new">
            New job
          </Button>
        }
      />

      {attention.length > 0 ? <Stack spacing={1.5}>{attention}</Stack> : null}

      {/* The four headline figures. */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        }}
      >
        <StatTile
          label="Mailable contacts"
          value={contacts.mailMergeSafe}
          tone="good"
          icon={<AlternateEmailOutlinedIcon fontSize="small" />}
          hint={`${mailableShare}% of ${formatNumber(contacts.total)} stored — an address at the company's own domain`}
          /*
           * `email=safe`, not `email=found`. "Has an address" is a strictly
           * larger set than "has an address we would mail": a founder's
           * university address is found and is not mailable, and a tile that
           * counted one and linked to the other sends people to a longer list
           * than the number they clicked.
           *
           * The value rides on the work-email key rather than a `grade` one of
           * its own, because the screen has one control for this and a link
           * naming a key that control does not read is a link that silently
           * opens the unfiltered list.
           *
           * `gate=` (any outcome) because this counts every stored contact, and the
           * Contacts screen opens on accepted people unless a link says otherwise —
           * so without it 3,550 opened a list of 3,342. Every contacts link on this
           * page names its gate for that reason.
           */
          to="/contacts?gate=&email=safe"
        />
        <StatTile
          label="Contacts stored"
          value={contacts.total}
          icon={<PeopleAltOutlinedIcon fontSize="small" />}
          hint={
            contacts.addedLast7Days > 0
              ? `${formatNumber(contacts.addedLast7Days)} added in the last 7 days`
              : 'None added in the last 7 days'
          }
          to="/contacts?gate="
        />
        <StatTile
          label="Companies with people"
          value={companies.withPeople}
          icon={<ApartmentOutlinedIcon fontSize="small" />}
          hint={`of ${formatNumber(companies.total)} profiled — ${formatNumber(companies.searchedNoPeople)} searched and empty`}
          to="/companies?outcome=with_people"
        />
        {credits ? (
          <StatTile
            label="Credits per mailable contact"
            value={credits.perMailMergeSafeContact}
            tone="brand"
            icon={<TokenOutlinedIcon fontSize="small" />}
            hint={`${formatNumber(credits.used)} spent this month over ${formatNumber(
              contacts.mailableThisMonth,
            )} mailable contacts found in it. Rows nobody can mail are not in the denominator.`}
          />
        ) : null}
      </Box>

      {reach ? (
        /*
         * Who can actually be reached, and by what. Over ACCEPTED people, because that is
         * the list these open and the only people an export carries — a rejected
         * namesake with a phone number is not someone to call.
         */
        <Box>
          <SectionHeader
            title="Who you can reach"
            description={`Of ${formatNumber(reach.accepted)} accepted contacts — the people on the Contacts screen and in exports.`}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: fromCrm !== null ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
              },
            }}
          >
            <StatTile
              label="With a mailable email"
              value={reach.mailable}
              tone="good"
              icon={<AlternateEmailOutlinedIcon fontSize="small" />}
              hint={`${share(reach.mailable, reach.accepted)}% of accepted — an address at the company’s own domain`}
              to="/contacts?gate=accepted&email=safe"
            />
            <StatTile
              label="With a phone number"
              value={reach.phone}
              tone="good"
              icon={<PhoneOutlinedIcon fontSize="small" />}
              hint={`${share(reach.phone, reach.accepted)}% of accepted`}
              to="/contacts?gate=accepted&phone=found"
            />
            <StatTile
              label="With both"
              value={reach.both}
              tone="good"
              icon={<ContactPhoneOutlinedIcon fontSize="small" />}
              hint={`A mailable email and a phone number · ${formatNumber(reach.mailable - reach.both)} more have the email alone`}
              to="/contacts?gate=accepted&email=safe&phone=found"
            />
            {fromCrm !== null ? (
              <StatTile
                label="Held in Zoho CRM"
                value={fromCrm}
                tone="good"
                icon={<CloudDoneOutlinedIcon fontSize="small" />}
                hint="Imported from the CRM or pushed to it — never bought from a provider again"
                to="/contacts?gate=accepted&crm=zoho"
              />
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        {/* What has been bought for the people we hold. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="Contact coverage"
              description="Where each lookup got to. “Not asked” is work still available; “nothing there” is a dead end already paid for."
            />
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Work email
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={contacts.total}
                    parts={lookupParts(contacts.byWorkEmailState, (s) => `/contacts?gate=&email=${s}`)}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Phone
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={contacts.total}
                    parts={lookupParts(contacts.byPhoneState, (s) => `/contacts?gate=&phone=${s}`)}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Whether the gates are admitting the right people. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="Admission and outreach"
              description="What the gates decided, and what happened after the send."
            />
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Gate outcome
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={contacts.total}
                    parts={[
                      {
                        key: 'accepted',
                        label: 'Accepted',
                        tone: 'good',
                        value: contacts.byGate['accepted'] ?? 0,
                        to: '/contacts?gate=accepted',
                      },
                      {
                        key: 'needs_review',
                        label: 'Needs review',
                        tone: 'info',
                        value: contacts.byGate['needs_review'] ?? 0,
                        to: '/contacts?gate=needs_review',
                      },
                      {
                        key: 'rejected',
                        label: 'Rejected',
                        tone: 'warning',
                        value: contacts.byGate['rejected'] ?? 0,
                        to: '/contacts?gate=rejected',
                      },
                      {
                        key: 'unknown',
                        label: 'Unmatched',
                        tone: 'neutral',
                        value: contacts.byGate['unknown'] ?? 0,
                      },
                    ]}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Outreach status
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={contacts.total}
                    parts={[
                      {
                        key: 'new',
                        label: 'Not contacted',
                        tone: 'neutral',
                        value: contacts.byOutreach['new'] ?? 0,
                        to: '/contacts?gate=&outreach=new',
                      },
                      {
                        key: 'contacted',
                        label: 'Contacted',
                        tone: 'info',
                        value: contacts.byOutreach['contacted'] ?? 0,
                        to: '/contacts?gate=&outreach=contacted',
                      },
                      {
                        key: 'replied',
                        label: 'Replied',
                        tone: 'good',
                        value: contacts.byOutreach['replied'] ?? 0,
                        to: '/contacts?gate=&outreach=replied',
                      },
                      {
                        key: 'not_interested',
                        label: 'Not interested',
                        tone: 'warning',
                        value: contacts.byOutreach['not_interested'] ?? 0,
                        to: '/contacts?gate=&outreach=not_interested',
                      },
                      {
                        key: 'do_not_contact',
                        label: 'Do not contact',
                        tone: 'bad',
                        value: contacts.byOutreach['do_not_contact'] ?? 0,
                        to: '/contacts?gate=&outreach=do_not_contact',
                      },
                    ]}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/*
        What a search actually returns, and why it so often returns nothing.
        These two belong side by side: the yield is the finding and the broken
        websites are most of the explanation for it.
      */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="What a search returns"
              description="People found per company actually searched. Companies nobody has asked about yet are not in this — they would make the yield fall on every upload and climb as it is worked."
            />
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                {companies.yield.mean.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                people per company · {formatNumber(companies.yield.contactsFound)} found across{' '}
                {formatNumber(companies.yield.searched)} searched
              </Typography>
            </Stack>
            <Breakdown total={companies.yield.searched} parts={yieldParts(companies.yield.distribution)} />

            {companies.fixableWebsites > 0 ? (
              /*
               * The one failure a person can fix, and not a footnote: a dead or
               * unrelated website means the ladder searched the wrong thing, so
               * the company is not one with nobody at it — it is one nobody has
               * actually looked for yet.
               */
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                action={
                  <Button component={RouterLink} to="/companies?domain=NXDOMAIN" size="small" color="inherit">
                    Show
                  </Button>
                }
              >
                <AlertTitle>
                  {formatNumber(companies.fixableWebsites)} companies have a website that is dead, unreachable
                  or unrelated
                </AlertTitle>
                {Math.round((companies.fixableWebsites / Math.max(1, companies.total)) * 100)}% of every
                company stored. A corrected address in the sheet is what rescues these, and nothing else will.
              </Alert>
            ) : null}

            {data.repairs && data.repairs.examined > 0 ? (
              /*
               * What the repair ladder made of them. Retired and corrected are
               * the clean-up done without anyone; queued is the work a person
               * still has; failed is what waits on credits or a key.
               */
              <Alert
                severity={data.repairs.queued > 0 ? 'info' : 'success'}
                sx={{ mt: 2 }}
                action={
                  <Button component={RouterLink} to="/repairs" size="small" color="inherit">
                    Open
                  </Button>
                }
              >
                <AlertTitle>
                  Repair ladder: {formatNumber(data.repairs.examined)} examined,{' '}
                  {formatNumber(data.repairs.autoApplied)} settled without a person
                </AlertTitle>
                {formatNumber(data.repairs.byStatus['retired'] ?? 0)} retired as dead ·{' '}
                {formatNumber(data.repairs.byVerdict['corrected'] ?? 0)} corrected ·{' '}
                {formatNumber(data.repairs.queued)} waiting for a decision ·{' '}
                {formatNumber(data.repairs.failed)} could not be examined yet
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="The addresses we hold"
              description="Graded over the contacts that HAVE an address — one nobody has looked up yet is not a grade. Only the top two may be mailed."
            />
            {Object.keys(contacts.byEmailConfidence).length === 0 ? (
              <EmptyState
                dense
                title="No addresses yet"
                description="Work emails are bought per contact. Select people on the Contacts screen and choose Enrich."
              />
            ) : (
              <Breakdown
                total={Object.values(contacts.byEmailConfidence).reduce((a, b) => a + b, 0)}
                parts={partsFrom(contacts.byEmailConfidence, GRADE_TONES)}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {/* Whether the persona presets are buying the people they promise to. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="Who we are finding"
              description="The presets exist to buy founders and C-suite rather than coordinators. This is them marking their own homework."
            />
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Seniority
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={Object.values(contacts.bySeniority).reduce((a, b) => a + b, 0)}
                    parts={partsFrom(
                      contacts.bySeniority,
                      BAND_TONES,
                      (band) => `/contacts?gate=&seniority=${band}`,
                    )}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Where they are
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Breakdown
                    total={Object.values(contacts.byCountry).reduce((a, b) => a + b, 0)}
                    parts={topCountries}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Which of the three sources is worth asking. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="How each source performs"
              description="What each provider was asked for and what came back. A lookup that finds nothing is still charged for by two of the three."
            />
            {providerRows.length === 0 ? (
              <EmptyState
                dense
                title="No lookups recorded yet"
                description="Every work email, personal email and phone number bought from here on is recorded against the source that answered it."
              />
            ) : (
              <Stack spacing={1.5}>
                {providerRows.map((row) => (
                  <Box key={row.provider}>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                        {SOURCE_LABEL[row.provider as SourceId] ?? row.provider}
                      </Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.rate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        of {formatNumber(row.attempts)} asked
                        {row.credits === undefined ? '' : ` · ${formatNumber(row.credits)} credits`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={row.rate}
                      sx={{ mt: 0.5, height: 6, borderRadius: 999 }}
                    />
                  </Box>
                ))}
                {/*
                  Said plainly rather than left to be assumed. Attempts have only
                  been recorded since the provenance model shipped, so this is a
                  rate over those lookups and not over everything stored — and a
                  rate whose denominator goes unstated gets read as the whole
                  picture.
                */}
                <Typography variant="caption" color="text.secondary">
                  Recorded for {formatNumber(sources.covered)} of {formatNumber(contacts.total)} contacts —
                  every lookup since source tracking began.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
        }}
      >
        {/* Recent runs. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="Recent jobs"
              description="The last few company lists put through the pipeline."
              actions={
                <Button size="small" component={RouterLink} to="/jobs">
                  All jobs
                </Button>
              }
            />
            {jobs.recent.length === 0 ? (
              <EmptyState
                dense
                title="No jobs yet"
                description="Upload a spreadsheet of target companies and the pipeline will find the decision-makers at each one."
                action={
                  <Button
                    variant="contained"
                    size="small"
                    component={RouterLink}
                    to="/jobs/new"
                    startIcon={<AddIcon />}
                  >
                    New job
                  </Button>
                }
              />
            ) : (
              <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {jobs.recent.map((job) => {
                  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0
                  return (
                    <Box
                      key={job.jobId}
                      component={RouterLink}
                      to={`/jobs/${job.jobId}`}
                      sx={{
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                        py: 1.25,
                        px: 1,
                        mx: -1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {job.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatNumber(job.total)} companies · {formatNumber(job.contactsFound)} contacts
                            {job.creditsUsed === undefined
                              ? ''
                              : ` · ${formatNumber(job.creditsUsed)} credits`}
                          </Typography>
                        </Box>
                        <JobStatusChip status={job.status} />
                      </Stack>
                      {job.status === 'RUNNING' || job.status === 'PAUSED' ? (
                        <Tooltip title={`${job.processed} of ${job.total} companies processed`}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{ mt: 1, height: 4, borderRadius: 999 }}
                          />
                        </Tooltip>
                      ) : null}
                    </Box>
                  )
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Where the input list itself is the problem. */}
        <Card variant="outlined">
          <CardContent>
            <SectionHeader
              title="Company outcomes"
              description="A company that was never searched is work available; one searched and empty is not."
            />
            <Breakdown
              total={companies.total}
              parts={[
                {
                  key: 'people',
                  label: 'People found',
                  tone: 'good',
                  value: companies.withPeople,
                  to: '/companies?outcome=with_people',
                },
                {
                  key: 'empty',
                  label: 'Searched, empty',
                  tone: 'warning',
                  value: companies.searchedNoPeople,
                  to: '/companies?outcome=no_people',
                },
                {
                  key: 'never',
                  label: 'Never searched',
                  tone: 'neutral',
                  value: companies.neverSearched,
                  to: '/companies?outcome=never_searched',
                },
              ]}
            />
            {credits ? (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  This month’s credits
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography
                    sx={{ fontSize: '1.25rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(credits.used)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {formatNumber(credits.cap)}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${capUsed}%`}
                    color={capTone(capUsed, 'default')}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={capUsed}
                  color={capTone(capUsed, 'primary')}
                  sx={{ mt: 1, height: 6, borderRadius: 999 }}
                />
              </Box>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  )
}
